import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { getMenuBadges, postVisitSection } from "../helpers/api";
import { APICore } from "../helpers/api/apiCore";

export interface MenuBadgesState {
  orders: number;
  expeditions: number;
  requests: number;
  shippings: number;
  invoices: number;
  users: number;
}

export type MenuVisitSection =
  | "orders"
  | "expeditions"
  | "requests"
  | "shippings"
  | "invoices"
  | "users";

interface MenuBadgesContextType {
  badges: MenuBadgesState;
  fetchBadges: () => Promise<void>;
  visitSection: (section: MenuVisitSection) => Promise<void>;
}

const defaultBadges: MenuBadgesState = {
  orders: 0,
  expeditions: 0,
  requests: 0,
  shippings: 0,
  invoices: 0,
  users: 0,
};

const MenuBadgesContext = createContext<MenuBadgesContextType | undefined>(
  undefined
);

const api = new APICore();
const rolesWithBadges = ["admin", "moderator", "supervisor", "seller", "agent"];
const shouldFetchBadges = () => rolesWithBadges.includes(api.getLoggedInUser()?.role || "");

export const MenuBadgesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [badges, setBadges] = useState<MenuBadgesState>(defaultBadges);

  const fetchBadges = useCallback(async () => {
    if (!shouldFetchBadges()) return;
    try {
      const res = await getMenuBadges();
      const data = res?.data;
      if (data && typeof data.orders === "number") {
        setBadges({
          orders: data.orders ?? 0,
          expeditions: data.expeditions ?? 0,
          requests: data.requests ?? 0,
          shippings: data.shippings ?? 0,
          invoices: data.invoices ?? 0,
          users: data.users ?? 0,
        });
      }
    } catch {
      setBadges(defaultBadges);
    }
  }, []);

  const visitSection = useCallback(
    async (section: MenuVisitSection) => {
      if (!shouldFetchBadges()) return;
      try {
        await postVisitSection(section);
        await fetchBadges();
      } catch {
        setBadges((prev) => ({
          ...prev,
          ...(section === "orders" && { orders: 0 }),
          ...(section === "expeditions" && { expeditions: 0 }),
          ...(section === "requests" && { requests: 0 }),
          ...(section === "shippings" && { shippings: 0 }),
          ...(section === "invoices" && { invoices: 0 }),
          ...(section === "users" && { users: 0 }),
        }));
      }
    },
    [fetchBadges]
  );

  useEffect(() => {
    if (shouldFetchBadges()) fetchBadges();
  }, [fetchBadges]);

  return (
    <MenuBadgesContext.Provider
      value={{ badges, fetchBadges, visitSection }}
    >
      {children}
    </MenuBadgesContext.Provider>
  );
};

export function useMenuBadges(): MenuBadgesContextType {
  const ctx = useContext(MenuBadgesContext);
  if (!ctx) {
    return {
      badges: defaultBadges,
      fetchBadges: async () => {},
      visitSection: async () => {},
    };
  }
  return ctx;
}
